import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  FormArray,
  FormControl,
  Validators,
  AbstractControl
} from '@angular/forms';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { ProductService } from 'app/services/product/product.service';
import { LookupCacheService } from 'app/services/lookup-cache/lookup-cache.service';

import {
  IDropdownSettings,
  NgMultiSelectDropDownModule
} from 'ng-multiselect-dropdown';

import { ImageItem } from 'app/services/gallery/gallery.service';
import { GalleryPickerComponent } from 'app/shared/gallery-picker/gallery-picker.component';
import { ImageUrlPipe } from 'app/modules/admin/pipe/image-url.pipe';
import { ImageFallbackDirective } from 'app/modules/admin/directives/ImageFallbackDirective';

type LookupOption = {
  id: number | string;
  name: string;
};

@Component({
  selector: 'app-add-product',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgMultiSelectDropDownModule,
    GalleryPickerComponent,
    ImageUrlPipe,
    ImageFallbackDirective
  ],
  templateUrl: './add-product.component.html',
  styleUrls: ['./add-product.component.css']
})
export class AddProductComponent implements OnInit {
  productForm!: FormGroup;

  activeTab = 'basic';
  submitted = false;
  isLoading = false;
  isSaving = false;
  errorMessage = '';

  isGalleryModalOpen = false;
  selectedPhotos: ImageItem[] = [];
  selectedPhotoIds: number[] = [];

  tabs = [
    { key: 'basic', labelAr: 'المعلومات الأساسية', labelEn: 'Basic' },
    { key: 'price', labelAr: 'السعر والمخزون', labelEn: 'Price & Stock' },
    { key: 'media', labelAr: 'المتغيرات , الوسائط', labelEn: 'Files & Variants' }
  ];

  sources: LookupOption[] = [
    { id: 'main', name: 'رئيسي' },
    { id: 'sub', name: 'فرعي' }
  ];

  mainCategories: LookupOption[] = [];
  subCategories: LookupOption[] = [];
  brands: LookupOption[] = [];
  units: LookupOption[] = [];
  variants: LookupOption[] = [];
  stores: LookupOption[] = [];

  // لو لسه محتاجهم ثابتين عندك
  branches: LookupOption[] = [{ id: 1, name: 'Family Hyper هايبر العائلة' }];
  sellers: LookupOption[] = [{ id: 1, name: 'البائع الرئيسي' }];

  // نفس أسماء edit-product
  multiDropdownSettings: IDropdownSettings = {
    singleSelection: false,
    idField: 'id',
    textField: 'name',
    selectAllText: 'تحديد الكل',
    unSelectAllText: 'إلغاء الكل',
    allowSearchFilter: true,
    searchPlaceholderText: 'بحث...',
    itemsShowLimit: 4,
    closeDropDownOnSelection: false
  };

  // نفس أسماء edit-product
  singleDropdownSettings: IDropdownSettings = {
    singleSelection: true,
    idField: 'id',
    textField: 'name',
    allowSearchFilter: true,
    searchPlaceholderText: 'بحث...',
    itemsShowLimit: 1,
    closeDropDownOnSelection: true
  };

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private productService: ProductService,
    private lookupCacheService: LookupCacheService
  ) {}

  async ngOnInit(): Promise<void> {
    this.createForm();
    await this.loadPageData();
  }

  private createForm(): void {
    this.productForm = this.fb.group({
      sourceType: ['sub', Validators.required],

      // مهم: نفس edit-product، قيمة الـ dropdown تكون array من object مش id مباشر
      mainCategoryId: [[], Validators.required],
      subCategoryIds: [[]],
      brandId: [[]],//, Validators.required],

      parentId: [],
      status: [true],
      numOfSale: [0, [Validators.min(0)]],

      branchId: [1, Validators.required],
      sellerId: [1, Validators.required],
      storeId: [1, Validators.required],

      arName: ['', [Validators.required, Validators.minLength(2)]],
      enName: ['', [Validators.required, Validators.minLength(2)]],
      arDescription: [''],
      enDescription: [''],

      purchasePrice: [0, [Validators.required, Validators.min(0)]],
      sellingPrice: [0, [Validators.required, Validators.min(0)]],
      sku: ['', Validators.required],
      barcode: ['', Validators.required],

      stocks: this.fb.array([]),

      published: [true],
      approved: [false],
      isFeatured: [false],
      isTodaysDeal: [false],
      isBestSeller: [false],

      discount: [0, [Validators.min(0), Validators.max(100)]],
      maxPurchaseQty: [0, [Validators.required, Validators.min(0)]],

      variantId: [null],
      unitId: [null, Validators.required],
      unitFactor: ['1', Validators.required],
      barcodeType: ['0', Validators.required],

      weightKg: [0, [Validators.min(0)]],
      minPurchaseQty: [1, [Validators.required, Validators.min(1)]],

      slug: [
  '',
  [
    Validators.required,
    Validators.pattern(/^[a-zA-Z0-9-]+$/)
  ]
],
      photosIdsText: ['']
    });

    this.addStock();

    this.productForm.get('sourceType')?.valueChanges.subscribe(value => {
      if (value === 'main') {
        this.productForm.patchValue({ subCategoryIds: [] });
      }
    });
  }

  // نفس edit-product: تحميل كل الـ lookups من LookupCacheService
  private async loadPageData(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';

    try {
      const lookups = await firstValueFrom(
        this.lookupCacheService.getLookups()
      );

      this.mainCategories = lookups.mainCategories || [];
      this.subCategories = lookups.subCategories || [];
      this.brands = lookups.brands || [];
      this.units = lookups.units || [];
      this.variants = lookups.variants || [];
      this.stores = lookups.stores || [];
    } catch (error) {
      console.error('Lookup loading error:', error);
      this.errorMessage = 'حدث خطأ أثناء تحميل بيانات الاختيارات.';
    } finally {
      this.isLoading = false;
    }
  }

  // نفس edit-product: Refresh cache
  async refreshLookups(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';

    try {
      const lookups = await firstValueFrom(
        this.lookupCacheService.refreshLookups()
      );

      this.mainCategories = lookups.mainCategories || [];
      this.subCategories = lookups.subCategories || [];
      this.brands = lookups.brands || [];
      this.units = lookups.units || [];
      this.variants = lookups.variants || [];
      this.stores = lookups.stores || [];

      alert('✅ تم تحديث بيانات الاختيارات بنجاح');
    } catch (error) {
      console.error('Refresh lookups error:', error);
      alert('❌ حدث خطأ أثناء تحديث بيانات الاختيارات');
    } finally {
      this.isLoading = false;
    }
  }

  get stocks(): FormArray {
    return this.productForm.get('stocks') as FormArray;
  }

  get mainCategoryControl(): FormControl {
    return this.productForm.get('mainCategoryId') as FormControl;
  }

  get subCategoryControl(): FormControl {
    return this.productForm.get('subCategoryIds') as FormControl;
  }

  get brandControl(): FormControl {
    return this.productForm.get('brandId') as FormControl;
  }

  private createStockGroup(data?: any): FormGroup {
    return this.fb.group({
      storeId: [Number(data?.store_id ?? data?.storeId ?? 1), Validators.required],
      quantity: [Number(data?.quantity ?? 0), [Validators.min(0)]],
      price: [Number(data?.price ?? 0), [Validators.min(0)]]
    });
  }

  addStock(data?: any): void {
    this.stocks.push(this.createStockGroup(data));
  }

  removeStock(index: number): void {
    if (this.stocks.length <= 1) return;
    this.stocks.removeAt(index);
  }

  setActiveTab(tabKey: string): void {
    this.activeTab = tabKey;
  }

  nextTab(): void {
    const currentIndex = this.tabs.findIndex(tab => tab.key === this.activeTab);
    if (currentIndex < this.tabs.length - 1) {
      this.activeTab = this.tabs[currentIndex + 1].key;
    }
  }

  previousTab(): void {
    const currentIndex = this.tabs.findIndex(tab => tab.key === this.activeTab);
    if (currentIndex > 0) {
      this.activeTab = this.tabs[currentIndex - 1].key;
    }
  }

  isFirstTab(): boolean {
    return this.activeTab === this.tabs[0].key;
  }

  isLastTab(): boolean {
    return this.activeTab === this.tabs[this.tabs.length - 1].key;
  }

  private getTabFields(tabKey: string): string[] {
    const tabFields: { [key: string]: string[] } = {
      basic: ['sourceType', 'mainCategoryId', 'brandId', 'branchId', 'sellerId', 'storeId', 'arName', 'enName'],
      price: ['purchasePrice', 'sellingPrice', 'sku', 'barcode', 'unitId', 'unitFactor', 'barcodeType', 'minPurchaseQty'],
      media: []
    };

    return tabFields[tabKey] || [];
  }

  isCurrentTabValid(): boolean {
    return this.getTabFields(this.activeTab)
      .every(field => this.productForm.get(field)?.valid !== false);
  }

  hasTabError(tabKey: string): boolean {
    if (!this.submitted) return false;

    return this.getTabFields(tabKey)
      .some(field => this.productForm.get(field)?.invalid);
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.productForm.get(fieldName);

    return !!(
      field &&
      field.invalid &&
      (field.touched || field.dirty || this.submitted)
    );
  }

  isDropdownInvalid(fieldName: string): boolean {
    const value = this.productForm.get(fieldName)?.value;

    return !!(
      this.submitted &&
      (!Array.isArray(value) || value.length === 0)
    );
  }

  getFieldError(fieldName: string): string {
    const field = this.productForm.get(fieldName);

    if (!field || !field.errors) return '';

    if (field.errors['required']) return 'هذا الحقل مطلوب';

    if (field.errors['min']) {
      return `القيمة يجب ألا تقل عن ${field.errors['min'].min}`;
    }

    if (field.errors['max']) {
      return `القيمة يجب ألا تزيد عن ${field.errors['max'].max}`;
    }

    if (field.errors['minlength']) {
      return `الحد الأدنى للحروف هو ${field.errors['minlength'].requiredLength}`;
    }

    return 'قيمة غير صحيحة';
  }

  // موجودة عشان لو الـ HTML الحالي فيه events عليها، والـ dropdown هيحدث الـ FormControl تلقائيًا
  onMainCategorySelect(_: any): void {}
  onMainCategoryDeSelect(_: any): void {}
  onBrandSelect(_: any): void {}
  onBrandDeSelect(_: any): void {}
  onSubCategorySelect(_: any): void {}
  onSubCategoryDeSelect(_: any): void {}

  onSubmit(): void {
    this.submitted = true;
    this.markFormGroupTouched(this.productForm);

    if (!this.validateFormBeforeSubmit()) {
      return;
    }

    const dto = this.buildCreateDto();

    console.log('Create DTO:', dto);

    this.createProduct(dto);
  }

   private validateFormBeforeSubmit(): boolean {
  const errors: string[] = [];

  if (this.productForm.invalid) {
    this.productForm.markAllAsTouched();

    const formErrors = this.getFormValidationErrors(this.productForm);

    if (formErrors.length > 0) {
      errors.push(...formErrors);
    } else {
      errors.push('يوجد حقول مطلوبة أو قيم غير صحيحة.');
    }
  }

  const mainCategoryId = this.getSingleDropdownId(
    this.productForm.value.mainCategoryId
  );

  if (!mainCategoryId) {
    errors.push('الفئة الرئيسية مطلوبة.');
  }

  if (this.stocks.length === 0) {
    errors.push('يجب إضافة مخزن واحد على الأقل.');
  }

  const storeIds = this.stocks.value
    .map((x: any) => Number(x.storeId))
    .filter((x: number) => Number.isFinite(x));

  const hasDuplicateStores = storeIds.some(
    (id: number, index: number) => storeIds.indexOf(id) !== index
  );

  if (hasDuplicateStores) {
    errors.push('لا يمكن تكرار نفس المتجر داخل المخزون.');
  }

  if (errors.length > 0) {
    alert('❌ يوجد أخطاء:\n\n' + errors.join('\n'));
    return false;
  }

  return true;
}

private getFormValidationErrors(form: any): string[] {
  const errors: string[] = [];

  Object.keys(form.controls).forEach(controlName => {
    const control = form.get(controlName);

    if (!control) return;

    // لو الحقل FormArray زي stocks
    if (control.controls && Array.isArray(control.controls)) {
      control.controls.forEach((arrayControl: any, index: number) => {
        Object.keys(arrayControl.controls || {}).forEach(childName => {
          const childControl = arrayControl.get(childName);

          if (childControl && childControl.errors) {
            errors.push(
              `المشكلة في المخزون رقم ${index + 1} - ${this.getFieldName(childName)}: ${this.getErrorMessage(childControl.errors)}`
            );
          }
        });
      });

      return;
    }

    if (control.errors) {
      errors.push(
        `${this.getFieldName(controlName)}: ${this.getErrorMessage(control.errors)}`
      );
    }
  });

  return errors;
}
private getFieldName(fieldName: string): string {
  const fields: any = {
    mainCategoryId: 'الفئة الرئيسية',
    subCategoryIds: 'الفئات الفرعية',
    brandId: 'البراند',
    parentId: 'المنتج الأب',
    status: 'الحالة',
    numOfSale: 'عدد البيع',

    branchId: 'الفرع',
    storeId: 'المخزن',

    arName: 'اسم المنتج بالعربي',
    enName: 'اسم المنتج بالإنجليزي',
    arDescription: 'الوصف بالعربي',
    enDescription: 'الوصف بالإنجليزي',

    purchasePrice: 'سعر الشراء',
    sellingPrice: 'سعر البيع',
    sku: 'SKU',
    barcode: 'الباركود',

    published: 'منشور',
    approved: 'موافق عليه',
    isFeatured: 'منتج مميز',
    isTodaysDeal: 'عرض اليوم',
    isBestSeller: 'الأكثر مبيعًا',

    discount: 'الخصم',
    maxPurchaseQty: 'أقصى كمية شراء',

    variantId: 'الفاريانت',
    unitId: 'الوحدة',
    unitFactor: 'معامل الوحدة',
    barcodeType: 'نوع الباركود',

    weightKg: 'الوزن بالكيلو',
    minPurchaseQty: 'أقل كمية شراء',

    slug: 'Slug',
    photosIdsText: 'صور المنتج'
  };

  return fields[fieldName] || fieldName;
}
private getErrorMessage(errors: any): string {
  if (errors.required) {
    return 'الحقل مطلوب';
  }

  if (errors.minlength) {
    return `أقل عدد حروف هو ${errors.minlength.requiredLength}`;
  }

  if (errors.min) {
    return `القيمة لا يجب أن تكون أقل من ${errors.min.min}`;
  }

  if (errors.max) {
    return `القيمة لا يجب أن تكون أكبر من ${errors.max.max}`;
  }

  if (errors.pattern) {
    return 'القيمة غير مطابقة للشكل المطلوب';
  }

  return 'قيمة غير صحيحة';
}


  private buildCreateDto(): any {
    const v = this.productForm.value;

    const mainCategoryId = this.getSingleDropdownId(v.mainCategoryId);
    const brandId = this.getSingleDropdownId(v.brandId);
    const subCategoryIds = this.getMultiDropdownIds(v.subCategoryIds);

    const stocks = (v.stocks || []).map((item: any) => ({
      storeId: item.storeId?.toString(),
      quantity: Number(item.quantity ?? 0),
      price: Number(item.price ?? 0)
    }));

    return {
      sourceType: v.sourceType,

      mainCategoryId: mainCategoryId?.toString() ?? null,
      subCategoryIds,

      parentId: v.parentId ? Number(v.parentId) : null,
      brandId: brandId ? Number(brandId) : null,

      status: !!v.status,
      numOfSale: Number(v.numOfSale ?? 0),

      branchId: v.branchId ? Number(v.branchId) : null,
      sellerId: v.sellerId?.toString() ?? null,
      storeId: v.storeId ? Number(v.storeId) : null,

      arName: v.arName ?? '',
      enName: v.enName ?? '',

      arDescription: v.arDescription ?? '',
      enDescription: v.enDescription ?? '',

      purchasePrice: Number(v.purchasePrice ?? 0),
      sellingPrice: Number(v.sellingPrice ?? 0),

      sku: v.sku ?? '',
      barcode: v.barcode ?? '',

      stocks,

      discount: Number(v.discount ?? 0),
      maxPurchaseQty: Number(v.maxPurchaseQty ?? 1),
      isBestSeller: !!v.isBestSeller,

      published: !!v.published,
      approved: !!v.approved,
      isFeatured: !!v.isFeatured,
      isTodaysDeal: !!v.isTodaysDeal,

      variantId: v.variantId ? Number(v.variantId) : null,

      unitId: v.unitId?.toString() ?? null,
      unitFactor: v.unitFactor?.toString() ?? '1',
      barcodeType: v.barcodeType?.toString() ?? '0',

      weightKg: Number(v.weightKg ?? 0),
      minPurchaseQty: Number(v.minPurchaseQty ?? 1),

      slug: v.slug ?? '',

      photos_ids: this.parseIdsText(v.photosIdsText)
    };
  }

  private createProduct(dto: any): void {
    this.isSaving = true;

    this.productService.addProduct(dto).subscribe({
      next: (response: any) => {
        console.log('Create response:', response);
        alert('✅ تم إنشاء المنتج بنجاح');
        //this.router.navigate(['/admin/products']);
      },
      error: (error: HttpErrorResponse) => {
        console.error('Create error:', error);
        this.handleError(error);
        this.isSaving = false;
      },
      complete: () => {
        this.isSaving = false;
      }
    });
  }

  onReset(): void {
    if (!confirm('هل تريد إعادة تعيين النموذج؟ سيتم فقدان جميع البيانات.')) return;

    this.productForm.reset({
      sourceType: 'sub',
      mainCategoryId: [],
      subCategoryIds: [],
      brandId: [],
      parentId: null,
      status: true,
      numOfSale: 0,
      branchId: 1,
      sellerId: 1,
      storeId: 1,
      purchasePrice: 0,
      sellingPrice: 0,
      published: true,
      approved: false,
      isFeatured: false,
      isTodaysDeal: false,
      isBestSeller: false,
      discount: 0,
      maxPurchaseQty: 0,
      variantId: null,
      unitId: null,
      unitFactor: '1',
      barcodeType: '0',
      weightKg: 0,
      minPurchaseQty: 1,
      photosIdsText: ''
    });

    this.stocks.clear();
    this.addStock();
    this.selectedPhotos = [];
    this.selectedPhotoIds = [];
    this.submitted = false;
  }

  onCancel(): void {
    if (confirm('هل تريد إلغاء التغييرات؟')) {
      this.router.navigate(['/admin/products']);
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else if (control instanceof FormArray) {
        control.controls.forEach((arrayControl: AbstractControl) => {
          if (arrayControl instanceof FormGroup) {
            this.markFormGroupTouched(arrayControl);
          } else {
            arrayControl.markAsTouched();
          }
        });
      } else {
        control?.markAsTouched();
      }
    });
  }

  private handleError(error: HttpErrorResponse): void {
    if (error.error?.errors) {
      console.error('Validation errors:', error.error.errors);
      alert('❌ يوجد أخطاء في البيانات. راجع Console.');
      return;
    }

    if (error.error?.message) {
      alert('❌ ' + error.error.message);
      return;
    }

    alert('❌ حدث خطأ أثناء إنشاء المنتج.');
  }

  private getSingleDropdownId(value: any): number | null {
    if (Array.isArray(value)) {
      const first = value[0];

      if (!first) return null;

      return Number(first.id ?? first);
    }

    if (value && typeof value === 'object') {
      return Number(value.id);
    }

    if (value === null || value === undefined || value === '') {
      return null;
    }

    return Number(value);
  }

  private getMultiDropdownIds(value: any): number[] {
    if (!Array.isArray(value)) return [];

    return value
      .map(item => Number(item?.id ?? item))
      .filter(id => Number.isFinite(id) && id > 0);
  }

  private parseIdsText(value: string): number[] {
    if (!value) return [];

    return value
      .split(',')
      .map(x => Number(x.trim()))
      .filter(id => Number.isFinite(id) && id > 0);
  }

  openGalleryModal(): void {
    this.isGalleryModalOpen = true;
  }

  closeGalleryModal(): void {
    this.isGalleryModalOpen = false;
  }

  onGalleryImagesChange(images: ImageItem[]): void {
    this.selectedPhotos = images || [];

    this.selectedPhotoIds = this.selectedPhotos
      .map(x => Number(x.id))
      .filter(id => Number.isFinite(id) && id > 0);

    this.patchPhotosIdsToForm();
  }

  removeSelectedPhoto(photo: ImageItem): void {
    this.selectedPhotos = this.selectedPhotos.filter(x => Number(x.id) !== Number(photo.id));

    this.selectedPhotoIds = this.selectedPhotos
      .map(x => Number(x.id))
      .filter(id => Number.isFinite(id) && id > 0);

    this.patchPhotosIdsToForm();
  }

  isGalleryImageSelected(image: ImageItem): boolean {
    return this.selectedPhotoIds.includes(Number(image.id));
  }

  private patchPhotosIdsToForm(): void {
    this.productForm.patchValue({
      photosIdsText: this.selectedPhotoIds.join(',')
    });

    this.productForm.get('photosIdsText')?.markAsDirty();
    this.productForm.get('photosIdsText')?.updateValueAndValidity();
  }
}
